import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import UsuariosAkasiaPOS from './UsuariosAkasiaPOS';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';
import ModalNuevoUsuarioAkasiaPOS from './Modales/ModalNuevoUsuarioAkasiaPOS';
import ModalEditarUsuarioAkasiaPOS from './Modales/ModalEditarUsuarioAkasiaPOS';
import ModalCrearSuperAdminAkasiaPOS from './Modales/ModalCrearSuperAdminAkasiaPOS';

/**
 * SeccionUsuariosAkasiaPOS
 * Gestiona la lógica de usuarios Akasia POS vinculados a una licencia
 * Siguiendo el patrón de SeccionUsuariosNubeAkasia
 */
const SeccionUsuariosAkasiaPOS = ({ idLicencia, licencias, infoSuscripcion }) => {
    const [usuarios, setUsuarios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showSuperAdminModal, setShowSuperAdminModal] = useState(false);
    const [usuarioSeleccionado, setUsuarioSeleccionado] = useState(null);

    const { token } = useToken();
    const { showErrorToast } = useToast();

    /**
     * Obtiene usuarios por licencia desde el backend
     */
    const obtenerUsuarios = async () => {
        setLoading(true);
        try {
            const res = await fetch(`/api/UsuariosAkasiaPOS/ObtenerUsuariosPorLicencia?idLicencia=${encodeURIComponent(idLicencia)}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!res.ok) throw new Error('Network response was not ok');

            const data = await res.json();
            // data expected to be ResponseType with detalles containing array or DataSet-like object
            if (data && data.iResponseType === 1 && data.detalles) {
                let detalles = data.detalles;
                try {
                    if (typeof detalles === 'string') detalles = JSON.parse(detalles);
                } catch (e) {
                    // leave as-is
                }

                let rows = [];
                if (detalles) {
                    if (Array.isArray(detalles)) {
                        rows = detalles;
                    } else if (detalles.Tables && Array.isArray(detalles.Tables)) {
                        const table = detalles.Tables.find(t => t.TableName && t.TableName.toLowerCase() === 'usuarios') || detalles.Tables[0];
                        if (table) {
                            if (Array.isArray(table.Rows)) rows = table.Rows;
                            else if (Array.isArray(table)) rows = table;
                        }
                    } else if (detalles.Table && Array.isArray(detalles.Table)) {
                        rows = detalles.Table;
                    } else if (detalles.usuarios && Array.isArray(detalles.usuarios)) {
                        rows = detalles.usuarios;
                    }
                }

                setUsuarios(rows || []);
            } else {
                setUsuarios([]);
                showErrorToast('No se pudieron obtener los usuarios');
            }
        } catch (err) {
            showErrorToast('Error al obtener usuarios');
            setUsuarios([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (idLicencia) obtenerUsuarios();
    }, [idLicencia]);

    /**
     * Callback cuando se crea un nuevo usuario
     */
    const onNuevoUsuarioCreado = () => {
        setShowModal(false);
        obtenerUsuarios();
    };

    /**
     * Callback cuando se actualiza un usuario
     */
    const onUsuarioActualizado = () => {
        setShowEditModal(false);
        setUsuarioSeleccionado(null);
        obtenerUsuarios();
    };

    /**
     * Callback cuando se crea el SuperAdmin
     */
    const onSuperAdminCreado = () => {
        setShowSuperAdminModal(false);
        obtenerUsuarios();
    };

    /**
     * Abre el modal de edición para un usuario
     */
    const handleEditarUsuario = (usuario) => {
        setUsuarioSeleccionado(usuario);
        setShowEditModal(true);
    };

    return (
        <>
            <UsuariosAkasiaPOS 
                usuarios={usuarios}
                loading={loading}
                licencias={licencias}
                onNuevoUsuario={() => setShowModal(true)}
                onEditarUsuario={handleEditarUsuario}
                onCrearSuperAdmin={() => setShowSuperAdminModal(true)}
            />

            <ModalNuevoUsuarioAkasiaPOS
                show={showModal}
                onHide={() => setShowModal(false)}
                idLicencia={idLicencia}
                licencias={licencias}
                onCreated={onNuevoUsuarioCreado}
                infoSuscripcionProp={infoSuscripcion}
            />

            <ModalEditarUsuarioAkasiaPOS
                show={showEditModal}
                onHide={() => {
                    setShowEditModal(false);
                    setUsuarioSeleccionado(null);
                }}
                usuario={usuarioSeleccionado}
                onUpdated={onUsuarioActualizado}
            />

            <ModalCrearSuperAdminAkasiaPOS
                show={showSuperAdminModal}
                onHide={() => setShowSuperAdminModal(false)}
                idLicencia={parseInt(idLicencia)}
                onCreated={onSuperAdminCreado}
            />
        </>
    );
};

SeccionUsuariosAkasiaPOS.propTypes = {
    idLicencia: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    licencias: PropTypes.shape({
        bIsActive: PropTypes.bool,
        bIsPeriodoPrueba: PropTypes.bool
    }),
    infoSuscripcion: PropTypes.object
};

export default SeccionUsuariosAkasiaPOS;
